import { httpService } from "./http.service";

export interface OrganizationUser {
  id: string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  status: "ACTIVE" | "PENDING_ACTIVATION" | "INACTIVE";
  emailVerified: boolean;
  phoneVerified: boolean;
  mfaEnabled: boolean;
  invitationToken: string | null;
  invitationTokenExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  roles: string[];
}

export interface InviteUserData {
  email: string;
  roles: string[];
}

export class UserManagementService {
  /**
   * Fetch all users belonging to a specific organization
   * @param organizationId - The unique identifier for the organization
   * @returns Promise<OrganizationUser[]> - List of users in the organization
   */
  static async getUsersByOrganization(organizationId: string): Promise<OrganizationUser[]> {
    try {
      const response = await httpService.get<OrganizationUser[]>(
        `/api/users/byOrg/${organizationId}`
      );
      return response;
    } catch (error) {
      console.error("Failed to fetch users by organization:", error);
      throw error;
    }
  }

  /**
   * Invite a new user to the organization
   * @param data - Email and roles for the new user
   * @returns Promise<OrganizationUser> - The newly created user
   */
  static async inviteUser(data: InviteUserData): Promise<OrganizationUser> {
    try {
      const response = await httpService.post<OrganizationUser>("/api/users/invite", data);
      return response;
    } catch (error) {
      console.error("Failed to invite user:", error);
      throw error;
    }
  }

  /**
   * Delete a user from the organization
   * @param userId - The unique identifier for the user to delete
   * @returns Promise<void>
   */
  static async deleteUser(userId: string): Promise<void> {
    try {
      await httpService.delete(`/api/users/${userId}`);
    } catch (error) {
      console.error("Failed to delete user:", error);
      throw error;
    }
  }

  static async removeMember(orgId: string, userId: string): Promise<void> {
    await httpService.delete(`/api/organisations/${orgId}/members/${userId}`);
  }

  static async removeSelf(orgId: string): Promise<void> {
    await httpService.delete(`/api/organisations/${orgId}/members/me`);
  }

  static async cancelInvitation(orgId: string, userId: string): Promise<void> {
    await httpService.delete(`/api/organisations/${orgId}/invitations/${userId}`);
  }

  static async grantRole(orgId: string, userId: string, role: string): Promise<void> {
    await httpService.post(`/api/organisations/${orgId}/members/${userId}/roles/${role}`);
  }

  static async revokeRole(orgId: string, userId: string, role: string): Promise<void> {
    await httpService.delete(`/api/organisations/${orgId}/members/${userId}/roles/${role}`);
  }
}
